import React, { type ReactNode, type RefObject } from "react";
import { C1_PaletteSearch } from "./C1_PaletteSearch";
import { usePaletteCaptionLayout } from "./usePaletteCaptionLayout";
import { hyphenatedCaption, paletteCaptionParts, stripSoftHyphens } from "../../../src/utils/softHyphen";
import "../../../src/components/Toolbar.scss";

export function C1_CreationPalette({ id, rootRef, search, children }: {
  id: string;
  rootRef: RefObject<HTMLDivElement>;
  search: Omit<React.ComponentProps<typeof C1_PaletteSearch>, "rootRef">;
  children: ReactNode;
}) {
  return (
    <div id={id} className="toolbar C1_furnitureToolbar C1_creationPalette" ref={rootRef}>
      <C1_PaletteSearch {...search} rootRef={rootRef} />
      <div id={`${id}Inner`} className="toolbarInner">{children}</div>
    </div>
  );
}

export function C1_PaletteChapter({
  id,
  title,
  collapsed,
  selected,
  singleChapter = false,
  layout = "tiles", // no itx
  onToggle,
  children,
}: {
  id: string;
  title: string;
  collapsed: boolean;
  selected?: boolean;
  singleChapter?: boolean;
  layout?:
    | "tiles" // no itx
    | "rows"; // no itx
  onToggle?: () => void;
  children: ReactNode;
}) {
  const itemsRef = usePaletteCaptionLayout(children, collapsed);
  return (
    <div
      className={`chapter${singleChapter ? " chapter--single" : ""}${collapsed ? " collapsed" : ""}${selected ? " selected" : ""}`}
      data-furniture-category={id}
      data-palette-chapter={id}
    >
      {(!singleChapter || onToggle) && (
        <div
          className="title landscape"
          data-palette-heading
          role="button" // no itx
          tabIndex={0}
          aria-expanded={!collapsed}
          onClick={onToggle}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggle?.();
            }
          }}
        >
          <span className="title-spacer" />
          <span className="title-text">{title}</span>
          <span className="title-icon">
            <i className={`las ${collapsed ? "la-angle-down" : "la-angle-up"}`} />
          </span>
        </div>
      )}
      {!collapsed && (
        <div className={`items${layout === "rows" ? " C1_paletteRows" : ""}`} ref={itemsRef}>
          {children}
        </div>
      )}
    </div>
  );
}

export function C1_PaletteItem({
  id,
  name,
  icon,
  active,
  language,
  dictionary = {},
  status,
  layout = "tiles", // no itx
  onSelect,
  onBeginPointerPlacement,
}: {
  id: string;
  name: string;
  icon?: ReactNode;
  layout?:
    | "tiles" // no itx
    | "rows"; // no itx
  active: boolean;
  language: string;
  dictionary?: Record<string, string>;
  status?: ReactNode;
  onSelect: (keepSelected: boolean, point?: { clientX: number; clientY: number }) => void;
  onBeginPointerPlacement?: (pointerId: number, clientX: number, clientY: number) => void;
}) {
  const plainName = stripSoftHyphens(name);
  const caption = hyphenatedCaption(plainName, dictionary);
  return (
    <div className={`tool${layout === "rows" ? " C1_paletteRow" : ""}`}>
      <div
        className={`toolInner${active ? " active" : ""}`}
        role="button" // no itx
        tabIndex={0}
        aria-pressed={active}
        title={plainName}
        aria-label={plainName}
        data-palette-item={id}
        onPointerDown={(event) => {
          if (event.button !== 0) return;
          event.currentTarget.dataset.paletteWasActive = String(active);
          onSelect(true, { clientX: event.clientX, clientY: event.clientY });
          onBeginPointerPlacement?.(event.pointerId, event.clientX, event.clientY);
        }}
        onClick={(event) => {
          if (event.detail === 0 || event.currentTarget.dataset.paletteWasActive === "true")
            onSelect(false, { clientX: event.clientX, clientY: event.clientY });
          delete event.currentTarget.dataset.paletteWasActive;
        }}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onSelect(false);
          }
        }}
      >
        <div
          className="consumerTemplate"
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: layout === "rows" ? "row" : "column",
            pointerEvents: "auto",
            justifyContent:
              layout === "rows"
                ? "flex-start" // no itx
                : "center",
          }}
        >
          {layout === "rows" ? (
            <>
              {icon && (
                <span className="C1_roomTemplateIcon" aria-hidden="true">
                  {icon}
                </span>
              )}
              <span className="C1_roomTemplateCheck" aria-hidden="true">
                {status}
              </span>
            </>
          ) : (
            <div className="C1_paletteIcon">
              {icon}
              {status && (
                <span className="C1_paletteItemStatus" aria-hidden="true">
                  {status}
                </span>
              )}
            </div>
          )}
          <div className="description" lang={language}>
            {paletteCaptionParts(caption).map((part, index) => (
              <React.Fragment key={index}>
                {part.unbreakable ? <span style={{ whiteSpace: "nowrap" }}>{part.text}</span> : part.text}
                {part.breakAfter && <wbr />}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
